import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Topbar from '../components/Topbar';
import DarkCard from '../components/DarkCard';
import DarkButton from '../components/DarkButton';
import ThemeRoot from '../components/ThemeRoot';
import { BadgeIcon } from '../svg/icons';
import { haptic } from '../lib/haptic';
import { useDarkStore } from '../store/darkStore';

export default function ProgressPage() {
  const navigate = useNavigate();
  const { xp, xpMax, streak, stages } = useDarkStore();

  useEffect(() => {
    haptic('levelUp');
  }, []);

  const pct = Math.min(100, Math.round((xp / xpMax) * 100));
  const doneCount = stages.filter((s) => s.status === 'done').length;

  return (
    <ThemeRoot>
      <div>
        <Topbar />

        <main className="mx-auto max-w-3xl px-4 py-8 md:py-12">
          <div className="flex flex-col items-center text-center">
            <BadgeIcon size={120} label={String(stages[0]?.id ?? 1)} />
            <h1 className="mt-4 font-marker text-4xl text-amber md:text-5xl">
              Level up!
            </h1>
            <p className="mt-2 max-w-md font-gill text-gray-600 dark:text-ink/80">
              You cleared a stage and banked fresh XP. Keep the momentum.
            </p>
          </div>

          <DarkCard className="mt-8">
            <div className="font-marker text-[11px] uppercase tracking-[0.2em] text-amber">
              XP progress
            </div>
            {/* Mount-animated SVG XP bar */}
            <svg viewBox="0 0 320 18" className="mt-3 w-full">
              <defs>
                <linearGradient id="xpFillGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#FFE08A" />
                  <stop offset="100%" stopColor="#F5A623" />
                </linearGradient>
                <filter id="xpFillGlow" x="-10%" y="-50%" width="120%" height="200%">
                  <feGaussianBlur stdDeviation="1.4" />
                  <feMerge>
                    <feMergeNode />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <rect x="0" y="2" width="320" height="14" rx="7" className="fill-gray-200 stroke-gray-300 dark:fill-base dark:stroke-white/10" />
              <rect
                x="2"
                y="4"
                height="10"
                rx="5"
                fill="url(#xpFillGrad)"
                filter="url(#xpFillGlow)"
                style={
                  {
                    width: `${(pct / 100) * 316}px`,
                    transformOrigin: 'left',
                    animation: 'xp-fill 900ms cubic-bezier(0.2, 0.9, 0.3, 1) both',
                    ['--xp-target' as string]: `${(pct / 100) * 316}px`,
                  } as React.CSSProperties
                }
              />
            </svg>
            <div className="mt-2 flex items-center justify-between font-gill text-sm text-gray-600 dark:text-ink/80">
              <span>
                {xp} / {xpMax} XP
              </span>
              <span>{pct}%</span>
            </div>
          </DarkCard>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <DarkCard>
              <div className="font-marker text-[11px] uppercase tracking-wider text-amber">
                Streak
              </div>
              <div className="mt-1 font-marker text-3xl text-gray-900 dark:text-ink">
                {streak}
              </div>
              <div className="font-gill text-xs text-gray-500 dark:text-ink/60">
                days in a row
              </div>
            </DarkCard>
            <DarkCard>
              <div className="font-marker text-[11px] uppercase tracking-wider text-mint-dim dark:text-mint">
                Stages
              </div>
              <div className="mt-1 font-marker text-3xl text-gray-900 dark:text-ink">
                {doneCount}
                <span className="text-gray-400 dark:text-ink/40"> / 6</span>
              </div>
              <div className="font-gill text-xs text-gray-500 dark:text-ink/60">
                done
              </div>
            </DarkCard>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {doneCount >= stages.length && (
              <DarkButton
                accent="amber"
                hapticKind="levelUp"
                onClick={() => navigate('/home/summary')}
              >
                See your Business Plan
              </DarkButton>
            )}
            <DarkButton
              accent="mint"
              hapticKind="levelUp"
              className="shadow-glow-mint"
              onClick={() => navigate('/home')}
            >
              Keep going
            </DarkButton>
          </div>
        </main>
      </div>
    </ThemeRoot>
  );
}
